/**
 * Placeholder rows shown inside PropertiesTable while the initial properties request is in
 * flight. Rendered within the table's shared Table.Body, so this returns the row fragment only.
 */
import { Box, Table, VisuallyHidden } from '@zillow/constellation-10';

import { LoadingImage, LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import {
  hiddenMobileCellStyles,
  lastRowStyles,
  overflowMenuCellStyle,
  propertyThumbnailStyles,
  unitCellContentStyles,
  unitInfoStyles,
} from '../styles';

type Props = {
  rowCount?: number;
};

// Default to a full page of placeholder rows so the initial-load skeleton matches the
// number of properties the API returns.
const SkeletonPropertyRows = ({ rowCount = PropertiesConstants.PROPERTIES_LIMIT_DEFAULT }: Props) => {
  return Array.from({ length: rowCount }, (_, skeletonIndex) => {
    const isLastSkeletonRow = skeletonIndex === rowCount - 1;

    return (
      <Table.Row
        key={`property-skeleton-${skeletonIndex}`}
        data-testid="PropertiesTable-SkeletonRow"
        css={isLastSkeletonRow ? lastRowStyles : undefined}
      >
        {/* address */}
        <Table.Cell>
          <Box css={unitCellContentStyles}>
            <Box css={propertyThumbnailStyles}>
              <LoadingImage isLoading={true} loadingWidth="100%" loadingHeight="100%" />
            </Box>
            <Box css={unitInfoStyles}>
              <LoadingText textStyle="body-sm-bold" isLoading={true} loadingWidth="120px">
                &nbsp;
              </LoadingText>
              <LoadingText textStyle="body-xs" isLoading={true} loadingWidth="140px">
                &nbsp;
              </LoadingText>
            </Box>
          </Box>
        </Table.Cell>

        {/* type */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <LoadingParagraph textStyle="body-sm" isLoading={true} loadingWidth="96px">
            &nbsp;
          </LoadingParagraph>
        </Table.Cell>

        {/* units */}
        <Table.Cell css={hiddenMobileCellStyles}>
          <LoadingParagraph textStyle="body-sm" isLoading={true} loadingWidth="120px">
            &nbsp;
          </LoadingParagraph>
        </Table.Cell>

        {/* overflow menu */}
        <Table.Cell css={overflowMenuCellStyle}>
          <VisuallyHidden>Loading</VisuallyHidden>
        </Table.Cell>
      </Table.Row>
    );
  });
};

export default SkeletonPropertyRows;
